import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaBolt,
  FaBoxOpen,
  FaClipboardCheck,
  FaClipboardList,
  FaCoins,
  FaCubes,
  FaFileInvoiceDollar,
  FaHistory,
  FaLayerGroup,
  FaRedoAlt,
  FaSave,
  FaSpinner,
  FaTruck
} from 'react-icons/fa';

const FILAMENT_OPTIONS = [
  { id: 'pla_standard', name: 'PLA Standard', price: 229 },
  { id: 'pla_plus', name: 'PLA+ (forstærket)', price: 259 },
  { id: 'petg', name: 'PETG', price: 249 },
  { id: 'abs', name: 'ABS', price: 269 },
  { id: 'flex', name: 'Fleksibel TPU', price: 320 },
  { id: 'nylon', name: 'Nylon', price: 380 }
];

const SHIPPING_RATES = {
  none: { label: 'Ingen fragt', tiers: [] },
  gls: { label: 'GLS', tiers: [39, 49, 69] },
  dao: { label: 'DAO', tiers: [37, 45, 65] },
  postnord: { label: 'PostNord', tiers: [42, 52, 75] }
};

const DEFAULT_FORM = {
  filamentType: '',
  filamentPrice: 229,
  weight: '',
  printTime: '00:00',
  watt: 220,
  kWhPrice: 3,
  wearAllowance: 1.5,
  hourlyRate: 500,
  manHours: '',
  profitMargin: '',
  shippingMethod: 'none',
  designHours: '',
  designHourlyRate: 650
};

const TABS = [
  { id: 'beregner', label: 'Prisberegner', icon: FaLayerGroup },
  { id: 'tilbud', label: 'Tilbud', icon: FaFileInvoiceDollar },
  { id: 'historik', label: 'Printlog', icon: FaHistory }
];

const lineIcons = {
  filament: <FaCubes className="text-indigo-500" />,
  power: <FaBolt className="text-yellow-500" />,
  wear: <FaBoxOpen className="text-orange-500" />,
  labor: <FaClipboardList className="text-blue-500" />,
  shipping: <FaTruck className="text-emerald-500" />,
  profit: <FaCoins className="text-fuchsia-500" />
};

const formatCurrency = (value = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0,00 kr';
  return `${num.toFixed(2)} kr`;
};

const formatMinutes = (minutes = 0) => {
  const total = Number(minutes) || 0;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours === 0) return `${rest} min`;
  return `${hours}t ${rest}m`;
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    const date = new Date(value);
    return date.toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' });
  } catch (_) {
    return value;
  }
};

const parseNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const resolveShippingCost = (method, weight) => {
  const option = SHIPPING_RATES[method];
  if (!option || option.tiers.length === 0) return 0;
  if (weight <= 0) return option.tiers[0];
  if (weight <= 500) return option.tiers[0];
  if (weight <= 1000) return option.tiers[1];
  return option.tiers[2];
};

const uniqueKey = () => `itm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const PrintCalculator = () => {
  const [activeTab, setActiveTab] = useState('beregner');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [offers, setOffers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ totalJobs: 0, totalOffers: 0, latestOffer: null });
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [copiedTotal, setCopiedTotal] = useState(false);
  const [copiedBreakdown, setCopiedBreakdown] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [savingJob, setSavingJob] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  const [offerError, setOfferError] = useState('');
  const [offerForm, setOfferForm] = useState({ description: '', shipping: '', discountPercent: '0' });
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerItems, setOfferItems] = useState([]);
  const [selectedLogJobId, setSelectedLogJobId] = useState('');
  const [editingOffer, setEditingOffer] = useState(null);
  const [loadingOfferDetails, setLoadingOfferDetails] = useState(false);
  const [discountEdit, setDiscountEdit] = useState({ offerId: null, value: '' });
  const [savingDiscountEdit, setSavingDiscountEdit] = useState(false);
  const discountCancelRef = useRef(false);

  const selectedFilament = useMemo(
    () => FILAMENT_OPTIONS.find((option) => option.id === form.filamentType),
    [form.filamentType]
  );

  const designPrice = useMemo(() => {
    const hours = parseNumber(form.designHours);
    const rate = parseNumber(form.designHourlyRate);
    return hours * rate;
  }, [form.designHours, form.designHourlyRate]);

  const calculation = useMemo(() => {
    const filamentPrice = parseNumber(form.filamentPrice);
    const weight = parseNumber(form.weight);
    const watt = parseNumber(form.watt);
    const kWhPrice = parseNumber(form.kWhPrice);
    const wear = parseNumber(form.wearAllowance);
    const hourlyRate = parseNumber(form.hourlyRate);
    const manHours = parseNumber(form.manHours);
    const profitMargin = parseNumber(form.profitMargin);
    const [hh = '0', mm = '0'] = (form.printTime || '0:0').split(':');
    const printMinutes = (Number(hh) || 0) * 60 + (Number(mm) || 0);
    const printHours = printMinutes / 60;

    const filamentCost = (filamentPrice / 1000) * weight;
    const electricityCost = (watt / 1000) * printHours * kWhPrice;
    const wearCost = wear * printHours;
    const laborCost = hourlyRate * manHours;
    const shippingCost = resolveShippingCost(form.shippingMethod, weight);
    const subtotal = filamentCost + electricityCost + wearCost + laborCost + shippingCost;
    const profit = subtotal * (profitMargin / 100);
    const totalExVat = subtotal + (Number.isFinite(profit) ? profit : 0);
    const totalInclVat = totalExVat * 1.25;

    const lines = [
      { key: 'filament', label: 'Filament', amount: filamentCost },
      { key: 'power', label: 'Strøm', amount: electricityCost },
      { key: 'wear', label: 'Slid', amount: wearCost },
      { key: 'labor', label: 'Mande timer', amount: laborCost },
      { key: 'shipping', label: 'Fragt', amount: shippingCost }
    ].filter((line) => line.amount > 0);

    if (profit > 0) {
      lines.push({ key: 'profit', label: 'Fortjeneste', amount: profit });
    }

    return {
      lines,
      shippingCost,
      subtotal,
      totalExVat,
      totalInclVat,
      printMinutes
    };
  }, [form]);

  const breakdownText = useMemo(() => {
    const lines = calculation.lines
      .map((line) => `${line.label}: ${line.amount.toFixed(2)} kr`)
      .join('\n');
    return `${lines}\nUden moms: ${formatCurrency(calculation.totalExVat)}\nMed moms: ${formatCurrency(calculation.totalInclVat)}`;
  }, [calculation]);

  const offerItemsTotal = useMemo(
    () => offerItems.reduce((sum, item) => sum + parseNumber(item.unitPrice) * item.quantity, 0),
    [offerItems]
  );

  const hasCurrentOfferItem = useMemo(
    () => offerItems.some((item) => item.source === 'current'),
    [offerItems]
  );

  const currentDesignPrice = useMemo(
    () => (editingOffer ? parseNumber(editingOffer.design_price) : designPrice),
    [editingOffer, designPrice]
  );

  const defaultShippingValue = editingOffer
    ? parseNumber(editingOffer.shipping_cost)
    : calculation.shippingCost || 0;

  const effectiveShippingValue =
    offerForm.shipping === '' ? defaultShippingValue : parseNumber(offerForm.shipping);

  const effectiveDiscountPercent = Math.min(
    100,
    Math.max(0, parseNumber(offerForm.discountPercent))
  );

  const offerSubtotal = currentDesignPrice + offerItemsTotal + effectiveShippingValue;
  const offerDiscountAmount = offerSubtotal * (effectiveDiscountPercent / 100);
  const offerTotalExVat = Math.max(0, offerSubtotal - offerDiscountAmount);
  const offerTotalInclVat = offerTotalExVat * 1.25;

  const buildHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const res = await axios.get('/api/print/stats');
      setStats(res.data || { totalJobs: 0, totalOffers: 0, latestOffer: null });
    } catch (error) {
      console.error('Kunne ikke hente stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchOffers = async () => {
    setLoadingOffers(true);
    try {
      const res = await axios.get('/api/print/offers');
      setOffers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Kunne ikke hente tilbud:', error);
    } finally {
      setLoadingOffers(false);
    }
  };

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await axios.get('/api/print/jobs');
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Kunne ikke hente jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOffers();
    fetchJobs();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilamentChange = (event) => {
    const selectedId = event.target.value;
    const selected = FILAMENT_OPTIONS.find((option) => option.id === selectedId);
    setForm((prev) => ({
      ...prev,
      filamentType: selectedId,
      filamentPrice: selected ? selected.price : prev.filamentPrice
    }));
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setFeedback({ type: '', message: '' });
    setCopiedBreakdown(false);
    setCopiedTotal(false);
  };

  const copyTotal = () => {
    if (!calculation.totalExVat) return;
    navigator.clipboard.writeText(calculation.totalExVat.toFixed(2));
    setCopiedTotal(true);
    setTimeout(() => setCopiedTotal(false), 1400);
  };

  const copyBreakdown = () => {
    navigator.clipboard.writeText(breakdownText);
    setCopiedBreakdown(true);
    setTimeout(() => setCopiedBreakdown(false), 1500);
  };

  const handleSavePrintJob = async () => {
    setFeedback({ type: '', message: '' });
    if (!calculation.totalExVat) {
      setFeedback({ type: 'error', message: 'Udfyld beregneren før du gemmer et printjob.' });
      return;
    }
    setSavingJob(true);
    try {
      await axios.post(
        '/api/print/jobs',
        {
          form,
          calculation,
          filamentLabel: selectedFilament?.name || 'Tilpasset',
          design: {
            hours: parseNumber(form.designHours),
            rate: parseNumber(form.designHourlyRate),
            price: designPrice
          }
        },
        { headers: buildHeaders() }
      );
      setFeedback({ type: 'success', message: 'Printjob gemt i historikken.' });
      fetchJobs();
      fetchStats();
    } catch (error) {
      console.error(error);
      setFeedback({ type: 'error', message: 'Kunne ikke gemme printjobbet. Prøv igen.' });
    } finally {
      setSavingJob(false);
    }
  };

  const handleOfferItemQuantityChange = (key, value) => {
    const quantity = Math.max(1, parseInt(value, 10) || 1);
    setOfferItems((prev) => prev.map((item) => (item.key === key ? { ...item, quantity } : item)));
  };

  const handleRemoveOfferItem = (key) => {
    setOfferItems((prev) => prev.filter((item) => item.key !== key));
  };

  const handleAddCurrentPrintToOffer = () => {
    if (!calculation.totalExVat) return;
    setOfferItems((prev) => {
      if (prev.some((item) => item.source === 'current')) return prev;
      return [
        ...prev,
        {
          key: 'current',
          jobId: null,
          label: `${selectedFilament?.name || 'Tilpasset print'} (beregning)`,
          unitPrice: parseNumber(calculation.totalExVat),
          quantity: 1,
          source: 'current'
        }
      ];
    });
  };

  const handleAddLogJobToOffer = () => {
    if (!selectedLogJobId) return;
    const job = jobs.find((entry) => entry.id === Number(selectedLogJobId));
    if (!job) return;
    setOfferItems((prev) => [
      ...prev,
      {
        key: uniqueKey(),
        jobId: job.id,
        label: `${job.filament_label || 'Printjob'} #${job.id}`,
        unitPrice: parseNumber(job.total_ex_vat),
        quantity: 1,
        source: 'existing'
      }
    ]);
    setSelectedLogJobId('');
  };

  const closeOfferModal = () => {
    setShowOfferModal(false);
    setOfferItems([]);
    setOfferError('');
    setSelectedLogJobId('');
    setEditingOffer(null);
    setLoadingOfferDetails(false);
    setDiscountEdit({ offerId: null, value: '' });
  };

  const startInlineDiscountEdit = (offer) => {
    if (savingDiscountEdit) return;
    setDiscountEdit({
      offerId: offer.id,
      value: String(offer.discount_percent ?? 0)
    });
  };

  const cancelInlineDiscountEdit = () => {
    if (savingDiscountEdit) return;
    discountCancelRef.current = true;
    setDiscountEdit({ offerId: null, value: '' });
    setTimeout(() => {
      discountCancelRef.current = false;
    }, 0);
  };

  const submitInlineDiscountEdit = async () => {
    if (!discountEdit.offerId) return;
    const percent = Math.min(100, Math.max(0, parseNumber(discountEdit.value)));
    setSavingDiscountEdit(true);
    try {
      await axios.patch(
        `/api/print/offers/${discountEdit.offerId}/discount`,
        { discountPercent: percent },
        { headers: buildHeaders() }
      );
      await fetchOffers();
      setFeedback({ type: 'success', message: 'Rabat opdateret.' });
    } catch (error) {
      console.error('Kunne ikke opdatere rabatten:', error);
      setFeedback({ type: 'error', message: 'Kunne ikke opdatere rabatten. Prøv igen.' });
    } finally {
      setSavingDiscountEdit(false);
      setDiscountEdit({ offerId: null, value: '' });
    }
  };

  const handleInlineDiscountKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitInlineDiscountEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelInlineDiscountEdit();
    }
  };

  const handleInlineDiscountBlur = () => {
    if (savingDiscountEdit || discountCancelRef.current) return;
    if (!discountEdit.offerId) return;
    submitInlineDiscountEdit();
  };

  const openNewOfferModal = () => {
    setEditingOffer(null);
    setLoadingOfferDetails(false);
    setDiscountEdit({ offerId: null, value: '' });
    setOfferForm({
      description: '',
      shipping: calculation.shippingCost ? calculation.shippingCost.toFixed(2) : '',
      discountPercent: '0'
    });
    setOfferError('');
    setSelectedLogJobId('');
    setOfferItems(
      calculation.totalExVat
        ? [
            {
              key: 'current',
              jobId: null,
              label: `${selectedFilament?.name || 'Tilpasset print'} (beregning)`,
              unitPrice: parseNumber(calculation.totalExVat),
              quantity: 1,
              source: 'current'
            }
          ]
        : []
    );
    setShowOfferModal(true);
  };

  const openOfferEditor = async (offerId) => {
    setOfferError('');
    setSelectedLogJobId('');
    setOfferItems([]);
    setLoadingOfferDetails(true);
    setDiscountEdit({ offerId: null, value: '' });
    setShowOfferModal(true);
    try {
      const res = await axios.get(`/api/print/offers/${offerId}`);
      const { offer, items } = res.data || {};
      if (!offer) {
        setOfferError('Tilbuddet kunne ikke findes.');
        return;
      }
      setEditingOffer(offer);
      setOfferForm({
        description: offer.description || '',
        shipping:
          offer.shipping_cost !== null && offer.shipping_cost !== undefined
            ? parseNumber(offer.shipping_cost).toFixed(2)
            : '',
        discountPercent:
          offer.discount_percent !== null && offer.discount_percent !== undefined
            ? String(offer.discount_percent)
            : '0'
      });
      setOfferItems(
        Array.isArray(items)
          ? items.map((item) => ({
              key: uniqueKey(),
              jobId: item.job_id,
              label: `${item.filament_label || 'Printjob'} #${item.job_id}`,
              unitPrice: parseNumber(item.unit_price ?? item.total_ex_vat),
              quantity: item.quantity || 1,
              source: 'existing'
            }))
          : []
      );
    } catch (error) {
      console.error('Kunne ikke hente tilbud:', error);
      setOfferError('Kunne ikke hente tilbuddet. Prøv igen.');
    } finally {
      setLoadingOfferDetails(false);
    }
  };

  const handleSubmitOffer = async () => {
    setOfferError('');
    if (!offerForm.description.trim()) {
      setOfferError('Tilføj en kort beskrivelse af opgaven.');
      return;
    }
    if (!offerItems.length) {
      setOfferError('Tilføj mindst ét print til tilbuddet.');
      return;
    }
    setSavingOffer(true);
    try {
      const payloadItems = offerItems.map((item) => ({
        jobId: item.jobId,
        unitPrice: item.unitPrice,
        quantity: item.quantity
      }));
      const resolvedShipping = effectiveShippingValue;
      const designPayload = editingOffer
        ? {
            hours: parseNumber(editingOffer.design_hours),
            rate: parseNumber(editingOffer.design_rate),
            price: currentDesignPrice
          }
        : {
            hours: parseNumber(form.designHours),
            rate: parseNumber(form.designHourlyRate),
            price: currentDesignPrice
          };
      const payload = {
        description: offerForm.description,
        shipping: resolvedShipping,
        discountPercent: effectiveDiscountPercent,
        form,
        calculation,
        filamentLabel: selectedFilament?.name || 'Tilpasset',
        items: payloadItems,
        design: designPayload
      };

      const url = editingOffer ? `/api/print/offers/${editingOffer.id}` : '/api/print/offers';
      const method = editingOffer ? axios.put : axios.post;
      const response = await method(url, payload, { headers: buildHeaders() });

      if (response.data?.offer) {
        fetchOffers();
      }
      if (response.data?.job) {
        setJobs((prev) => [response.data.job, ...prev]);
      }
      fetchStats();
      const message = editingOffer ? 'Tilbud opdateret.' : 'Tilbud gemt.';
      setFeedback({ type: 'success', message });
      closeOfferModal();
    } catch (error) {
      console.error(error);
      setOfferError('Kunne ikke gemme tilbuddet. Tjek felterne og prøv igen.');
    } finally {
      setSavingOffer(false);
    }
  };

  const statsCards = useMemo(() => [
    { label: 'Registrerede printjobs', value: loadingStats ? '…' : stats.totalJobs, icon: <FaLayerGroup className="text-indigo-200" /> },
    { label: 'Tilbud oprettet', value: loadingStats ? '…' : stats.totalOffers, icon: <FaFileInvoiceDollar className="text-pink-200" /> },
    {
      label: 'Seneste tilbud',
      value: stats.latestOffer ? formatCurrency(stats.latestOffer.total_ex_vat) : '-',
      icon: <FaCoins className="text-amber-200" />
    }
  ], [stats, loadingStats]);

  const renderCalculatorTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm space-y-6">
          <div>
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Design</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Timer</label>
                <input
                  type="number"
                  name="designHours"
                  min="0"
                  step="0.25"
                  value={form.designHours}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Timepris (kr)</label>
                <input
                  type="number"
                  name="designHourlyRate"
                  min="0"
                  step="25"
                  value={form.designHourlyRate}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Designpris</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatCurrency(designPrice)}</p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Printberegner</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Filament type</label>
                <select
                  name="filamentType"
                  value={form.filamentType}
                  onChange={handleFilamentChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                >
                  <option value="">Vælg filament</option>
                  {FILAMENT_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name} ({option.price} kr/kg)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Filament pris (kr/kg)</label>
                <input
                  type="number"
                  name="filamentPrice"
                  min="0"
                  step="0.5"
                  value={form.filamentPrice}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Vægt (gram)</label>
                <input
                  type="number"
                  name="weight"
                  min="0"
                  value={form.weight}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Printtid (hh:mm)</label>
                <input
                  type="time"
                  name="printTime"
                  value={form.printTime}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Forbrug (W)</label>
                <input
                  type="number"
                  name="watt"
                  min="0"
                  value={form.watt}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark-border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">kWh pris</label>
                <input
                  type="number"
                  name="kWhPrice"
                  min="0"
                  step="0.1"
                  value={form.kWhPrice}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Slid-tillæg (kr/time)</label>
                <input
                  type="number"
                  name="wearAllowance"
                  min="0"
                  step="0.1"
                  value={form.wearAllowance}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Mande time (kr)</label>
                <input
                  type="number"
                  name="hourlyRate"
                  min="0"
                  value={form.hourlyRate}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Antal mande timer</label>
                <input
                  type="number"
                  name="manHours"
                  min="0"
                  step="0.1"
                  value={form.manHours}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark-border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Fortjeneste (%)</label>
                <input
                  type="number"
                  name="profitMargin"
                  min="0"
                  step="0.5"
                  value={form.profitMargin}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark-border-gray-700 bg-white dark:bg-gray-800 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark	text-gray-300">Skal ordren fragtes?</label>
                <select
                  name="shippingMethod"
                  value={form.shippingMethod}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark-border-gray-700 bg-white dark:bg-gray-800 p-2"
                >
                  {Object.entries(SHIPPING_RATES).map(([key, option]) => (
                    <option key={key} value={key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resultat</h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyBreakdown}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                  copiedBreakdown ? 'border-green-200 bg-green-100 text-green-700' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                <FaClipboardCheck />
                {copiedBreakdown ? 'Kopieret!' : 'Kopiér beregning'}
              </button>
              <button
                type="button"
                onClick={copyTotal}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                  copiedTotal ? 'border-green-200 bg-green-100 text-green-700' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                {copiedTotal ? 'Kopiér total ✔' : 'Kopiér total'}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {calculation.lines.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">Udfyld felter for at se beregningen.</p>
            )}
            {calculation.lines.map((line) => (
              <div
                key={line.key}
                className="flex items-center justify-between rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 px-4 py-3"
              >
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                  {lineIcons[line.key] || lineIcons.filament}
                  <span>{line.label}</span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(line.amount)}</span>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4 space-y-2">
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
              <span>Print tid</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatMinutes(calculation.printMinutes)}</span>
            </div>
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
              <span>Uden moms</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {calculation.totalExVat ? formatCurrency(calculation.totalExVat) : '0,00 kr'}
              </span>
            </div>
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
              <span>Med moms</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {calculation.totalInclVat ? formatCurrency(calculation.totalInclVat) : '0,00 kr'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={handleSavePrintJob}
              disabled={savingJob}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-900 bg-gray-900 text-white py-3 font-semibold hover:bg-gray-800 disabled:opacity-60 dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {savingJob ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Gem printjob
            </button>
              <button
                type="button"
                onClick={openNewOfferModal}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 py-3 font-semibold text-gray-800 dark:text-gray-100"
            >
              <FaFileInvoiceDollar />
              Opret tilbud
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 py-3 font-semibold text-gray-700 dark:text-gray-200"
            >
              <FaRedoAlt />
              Nulstil felter
            </button>
          </div>
        </section>
      </div>
    </div>
  );

  const renderOffersTab = () => (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tilbud</p>
            <p className="text-gray-700 dark:text-gray-200 text-sm">Gemte tilbud fra 3D print.</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Alle viste beløb er ekskl. moms.</p>
          </div>
          <button
            type="button"
            onClick={openNewOfferModal}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-900 bg-gray-900 text-white px-4 py-2 text-sm font-semibold hover:bg-gray-800 dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            <FaFileInvoiceDollar />
            Nyt tilbud
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Beskrivelse</th>
                <th className="px-4 py-3 text-right font-semibold">Design (ex. moms)</th>
                <th className="px-4 py-3 text-right font-semibold">Print (ex. moms)</th>
                <th className="px-4 py-3 text-right font-semibold">Prints</th>
                <th className="px-4 py-3 text-right font-semibold">Fragt (ex. moms)</th>
                <th className="px-4 py-3 text-right font-semibold">Rabat</th>
                <th className="px-4 py-3 text-right font-semibold">Total (ex. moms)</th>
                <th className="px-4 py-3 text-left font-semibold">Dato</th>
                <th className="px-4 py-3 text-right font-semibold">Handling</th>
              </tr>
            </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {loadingOffers ? (
                <tr>
                  <td colSpan="9" className="px-4 py-6 text-center">
                  <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <FaSpinner className="animate-spin" /> Henter tilbud...
                  </div>
                </td>
              </tr>
            ) : offers.length === 0 ? (
              <tr>
                  <td colSpan="9" className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Ingen tilbud oprettet endnu.
                </td>
              </tr>
            ) : (
              offers.map((offer) => (
                <tr key={offer.id}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900 dark:text-white">{offer.description}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {offer.filament_label || 'Filament ukendt'}
                      {offer.weight_grams ? ` · ${offer.weight_grams} g` : ''}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">{formatCurrency(offer.design_price)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(offer.print_price)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="leading-tight">
                        <p>
                          {offer.line_count || 0} linje
                          {(offer.line_count || 0) === 1 ? '' : 'r'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {offer.total_quantity || 0} stk
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">{formatCurrency(offer.shipping_cost)}</td>
                    <td className="px-4 py-3 text-right">
                      {discountEdit.offerId === offer.id ? (
                        <div
                          className="flex items-center justify-end gap-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={discountEdit.value}
                            onChange={(e) =>
                              setDiscountEdit((prev) => ({ ...prev, value: e.target.value }))
                            }
                            onKeyDown={handleInlineDiscountKeyDown}
                            onBlur={handleInlineDiscountBlur}
                            autoFocus
                            disabled={savingDiscountEdit}
                            className="w-20 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-right"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-300">%</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startInlineDiscountEdit(offer)}
                          className="group inline-flex flex-col items-end text-right text-red-500 w-full"
                        >
                          <span>-{formatCurrency(offer.discount)}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-red-400">
                            ({offer.discount_percent || 0}% · klik for at redigere)
                          </span>
                        </button>
                      )}
                    </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(offer.total_ex_vat)}
                  </td>
                    <td className="px-4 py-3 text-left text-sm">{formatDate(offer.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openOfferEditor(offer.id)}
                        className="text-sm font-semibold text-falck-blue hover:text-falck-blue/80"
                      >
                        Rediger
                      </button>
                    </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderJobsTab = () => (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 dark border-gray-800">
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Printlog</p>
        <p className="text-gray-700 dark:text-gray-200 text-sm">Seneste printjobs fra beregneren.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Dato</th>
              <th className="px-4 py-3 text-left font-semibold">Filament</th>
              <th className="px-4 py-3 text-right font-semibold">Vægt</th>
              <th className="px-4 py-3 text-right font-semibold">Printtid</th>
              <th className="px-4 py-3 text-right font-semibold">Designpris</th>
              <th className="px-4 py-3 text-right font-semibold">Pris ekskl. moms</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
            {loadingJobs ? (
              <tr>
                <td colSpan="6" className="px-4 py-6 text-center">
                  <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <FaSpinner className="animate-spin" /> Henter printlog...
                  </div>
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Ingen gemte printjobs endnu.
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.id}>
                  <td className="px-4 py-3">{formatDate(job.created_at)}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900 dark:text-white">{job.filament_label || 'UKENDT'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{job.filament_type || ''}</p>
                  </td>
                  <td className="px-4 py-3 text-right">{job.weight_grams ? `${job.weight_grams} g` : '-'}</td>
                  <td className="px-4 py-3 text-right">{formatMinutes(job.print_minutes)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(job.design_price)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(job.total_ex_vat)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 sm:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase text-gray-500 dark:text-gray-400 tracking-wide">3D Print</p>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Prisberegning & log</h1>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={openNewOfferModal}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-900 bg-gray-900 px-5 py-3 text-sm font-semibold text-white hover:bg-gray-800 dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              <FaFileInvoiceDollar /> Nyt tilbud
            </button>
            <button
              type="button"
              onClick={handleSavePrintJob}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-900 bg-white px-5 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50 dark:border-white dark:bg-transparent dark:text-white"
              disabled={savingJob}
            >
              {savingJob ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Gem printjob
            </button>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {statsCards.map((card) => (
            <div key={card.label} className="rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 flex items-center gap-3">
              <div className="rounded-xl bg-white text-falck-blue dark:bg-gray-900 dark:text-white p-3">{card.icon}</div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{card.label}</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {feedback.message && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            feedback.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/40 dark:bg-red-900/40 dark:text-red-100'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/40 dark:text-emerald-100'
          }`}
        >
          {feedback.message}
        </div>
      )}

      <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="border-b border-gray-100 dark:border-gray-800 px-4 sm:px-6">
          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition ${
                    isActive
                      ? 'border-falck-blue text-falck-blue dark:text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100'
                  }`}
                >
                  <Icon /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {activeTab === 'beregner' && renderCalculatorTab()}
          {activeTab === 'tilbud' && renderOffersTab()}
          {activeTab === 'historik' && renderJobsTab()}
        </div>
      </section>

        {showOfferModal && (
          <div className="fixed inset-0 z-50 bg-black/60 px-4 py-6 sm:py-10 flex items-start justify-center overflow-y-auto">
            <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingOffer ? `Rediger tilbud${editingOffer.id ? ` #${editingOffer.id}` : ''}` : 'Opret tilbud'}
                </h3>
                <button onClick={closeOfferModal} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              </div>
              {loadingOfferDetails ? (
                <div className="py-10 text-center text-gray-500 dark:text-gray-400">
                  <FaSpinner className="mx-auto mb-3 animate-spin" />
                  Indlæser tilbud...
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Beskrivelse</label>
                    <input
                      type="text"
                      name="description"
                      value={offerForm.description}
                      onChange={(e) => setOfferForm((prev) => ({ ...prev, description: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                      placeholder="Beskriv kort opgaven"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Fragt (valgfri)</label>
                      <input
                        type="number"
                        name="shipping"
                        value={offerForm.shipping}
                        onChange={(e) => setOfferForm((prev) => ({ ...prev, shipping: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Rabat (%)</label>
                      <input
                        type="number"
                        name="discountPercent"
                        min="0"
                        max="100"
                        value={offerForm.discountPercent}
                        onChange={(e) => setOfferForm((prev) => ({ ...prev, discountPercent: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2"
                      />
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Prints i tilbud</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Tilføj flere jobs og juster antal.</p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={handleAddCurrentPrintToOffer}
                        disabled={hasCurrentOfferItem || !calculation.totalExVat}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-100 disabled:opacity-60"
                      >
                        Brug nuværende print
                      </button>
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedLogJobId}
                          onChange={(e) => setSelectedLogJobId(e.target.value)}
                          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-2 text-sm"
                        >
                          <option value="">Tilføj fra printlog</option>
                          {jobs.slice(0, 50).map((job) => (
                            <option key={job.id} value={job.id}>
                              #{job.id} · {job.filament_label || 'Printjob'} ({formatCurrency(job.total_ex_vat)})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleAddLogJobToOffer}
                          className="rounded-lg border border-gray-900 bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800 dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                        >
                          Tilføj
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {offerItems.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Ingen print tilføjet endnu.</p>
                    ) : (
                      offerItems.map((item) => (
                        <div
                          key={item.key}
                          className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900/40 px-3 py-2"
                        >
                          <div className="flex-1 min-w-[140px]">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {item.jobId ? `Job #${item.jobId}` : 'Ny beregning'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-500 dark:text-gray-400">Antal</label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleOfferItemQuantityChange(item.key, e.target.value)}
                              className="w-20 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                            />
                          </div>
                          <div className="text-right text-sm text-gray-600 dark:text-gray-300 w-20">
                            {formatCurrency(item.unitPrice)}
                          </div>
                          <div className="text-right font-semibold text-gray-900 dark:text-white w-24">
                            {formatCurrency(item.unitPrice * item.quantity)}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveOfferItem(item.key)}
                            className="text-gray-400 hover:text-red-500 text-lg leading-none"
                            aria-label="Fjern"
                          >
                            ✕
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Designpris</span>
                      <span className="font-semibold">{formatCurrency(currentDesignPrice)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Printpris</span>
                      <span className="font-semibold">{formatCurrency(offerItemsTotal)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Fragt</span>
                      <span className="font-semibold">{formatCurrency(effectiveShippingValue)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Rabat</span>
                      <span className="font-semibold text-red-500">
                        {effectiveDiscountPercent}% ({formatCurrency(offerDiscountAmount)})
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Total ekskl. moms</span>
                      <span className="font-semibold">{formatCurrency(offerTotalExVat)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Total inkl. moms</span>
                      <span className="font-semibold">{formatCurrency(offerTotalInclVat)}</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
                      <span>Print antal</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {offerItems.reduce((sum, item) => sum + item.quantity, 0)}
                      </span>
                    </div>
                  </div>
                  {offerError && (
                    <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900/40 rounded-md px-3 py-2">
                      {offerError}
                    </p>
                  )}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeOfferModal}
                      className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
                    >
                      Annuller
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitOffer}
                      disabled={savingOffer}
                      className="rounded-lg border border-gray-900 bg-gray-900 text-white px-4 py-2 text-sm font-semibold hover:bg-gray-800 disabled:opacity-60 inline-flex items-center gap-2 dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                    >
                      {savingOffer ? <FaSpinner className="animate-spin" /> : <FaFileInvoiceDollar />}
                      {editingOffer ? 'Opdater tilbud' : 'Gem tilbud'}
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintCalculator;
